import React, { useState } from 'react';
import './StyleEditor.css';

interface UserStyle {
  id?: number;
  user_id: number;
  style_summary: string;
  positive_lexicon: string[];
  negative_lexicon: string[];
  writing_style: {
    tone: string;
    formality: string;
    emoji_usage: string;
    paragraph_length: string;
  };
  examples: string[];
}

interface StyleEditorProps {
  style: UserStyle;
  onChange: (style: UserStyle) => void;
  saving: boolean;
}

const StyleEditor: React.FC<StyleEditorProps> = ({ style, onChange, saving }) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'lexicon' | 'writing' | 'examples' | 'channel'>('summary');
  const [channelUrl, setChannelUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');

  const handleStyleChange = (field: keyof UserStyle, value: any) => {
    const updatedStyle = { ...style, [field]: value };
    onChange(updatedStyle);
  };

  const handleWritingStyleChange = (field: string, value: string) => {
    const updatedWritingStyle = { ...style.writing_style, [field]: value };
    handleStyleChange('writing_style', updatedWritingStyle);
  };

  const handleLexiconChange = (type: 'positive_lexicon' | 'negative_lexicon', value: string) => {
    const words = value.split(',').map(word => word.trim()).filter(word => word.length > 0);
    handleStyleChange(type, words);
  };



  const addExample = () => {
    const newExample = prompt('Введите пример поста:');
    if (newExample && newExample.trim()) {
      const updatedExamples = [...style.examples, newExample.trim()];
      handleStyleChange('examples', updatedExamples);
    }
  };

  const removeExample = (index: number) => {
    const updatedExamples = style.examples.filter((_, i) => i !== index);
    handleStyleChange('examples', updatedExamples);
  };

  const extractChannelName = (url: string): string | null => {
    // Извлекаем имя канала из различных форматов URL
    const patterns = [
      /t\.me\/([a-zA-Z0-9_]+)/,
      /telegram\.me\/([a-zA-Z0-9_]+)/,
      /^@?([a-zA-Z0-9_]+)$/,
      /\/([a-zA-Z0-9_]+)$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  const analyzeChannel = async () => {
    if (!channelUrl.trim()) {
      alert('Пожалуйста, введите ссылку на канал');
      return;
    }

    const channelName = extractChannelName(channelUrl.trim());
    if (!channelName) {
      alert('Неверный формат ссылки на канал. Используйте формат: t.me/channelname или @channelname');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisProgress('🔍 Проверяем канал...');

    try {
      // Проверяем окружение
      const isDemo = !window.Telegram?.WebApp?.initDataUnsafe?.user || process.env.REACT_APP_DEMO_MODE === 'true';
      
      if (isDemo) {
        // Демо-режим - симуляция
        setAnalysisProgress('📡 Получаем последние 15 постов...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        setAnalysisProgress('🧠 Анализируем стиль письма...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        setAnalysisProgress('✨ Обновляем профиль стиля...');
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Симулируем результат анализа
        const analyzedStyle = {
          ...style,
          style_summary: `🎯 Анализ канала ${channelName}: Энергичный и вовлекающий стиль с использованием эмодзи и коротких абзацев для лучшего восприятия в мессенджере. Часто использует вопросы к аудитории и call-to-action.`,
          positive_lexicon: ['невероятно', 'потрясающе', 'вау', 'крутой', 'топ', 'огонь', 'бомба'],
          negative_lexicon: ['скучно', 'банально', 'неинтересно', 'старо'],
          writing_style: {
            tone: 'enthusiastic',
            formality: 'casual',
            emoji_usage: 'frequent', 
            paragraph_length: 'short'
          },
          examples: [
            `🔥 Крутая новость! Только что увидел невероятный проект, который точно изменит всю индустрию. Как думаете, готовы ли мы к таким переменам? 🤔`,
            `💡 Поделитесь в комментариях: какой последний тренд вас больше всего впечатлил? Я в шоке от того, что происходит в сфере ИИ! 🚀`,
            `⚡ Топ-3 ошибки, которые делают 90% новичков. Сохраняйте, чтобы не потерять! 📌`
          ]
        };

        onChange(analyzedStyle);
        setAnalysisProgress('✅ Анализ завершен!');
        
        setTimeout(() => {
          setIsAnalyzing(false);
          setAnalysisProgress('');
          setActiveTab('summary');
          alert('🎉 Анализ канала завершен! Профиль стиля обновлен на основе найденных постов. (демо-режим)');
        }, 1500);
        
      } else {
        // Реальный режим - вызов API
        const telegramUser = window.Telegram.WebApp.initDataUnsafe.user;
        
        if (!telegramUser) {
          throw new Error('Пользователь Telegram не найден');
        }
        
        setAnalysisProgress('📡 Получаем последние 15 постов...');
        
        // Вызываем API анализа канала
        const apiUrl = process.env.REACT_APP_CHANNEL_ANALYZER_URL || 'https://your-api-gateway-url/analyze-channel';
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            channel_url: channelUrl.trim(),
            user_id: telegramUser.id
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Анализ не удался');
        }

        setAnalysisProgress('✅ Анализ завершен!');
        
        // Применяем результат анализа
        const analyzedStyle = {
          ...style,
          ...result.analysis_result
        };

        onChange(analyzedStyle);
        
        setTimeout(() => {
          setIsAnalyzing(false);
          setAnalysisProgress('');
          setActiveTab('summary');
          alert(`🎉 Анализ канала завершен! Проанализировано ${result.posts_analyzed} постов из @${result.channel_name}.`);
        }, 1500);
      }

    } catch (error) {
      console.error('Ошибка анализа канала:', error);
      setAnalysisProgress('❌ Ошибка при анализе канала');
      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisProgress('');
      }, 2000);
      
      const errorMessage = error instanceof Error ? error.message : 'Произошла ошибка при анализе канала';
      alert(`Ошибка анализа: ${errorMessage}\n\nПроверьте:\n- Канал публичный и доступен\n- Канал содержит текстовые посты\n- Ссылка указана правильно`);
    }
  };

  return (
    <div className="style-editor">
      {/* Табы */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          📝 Описание
        </button>
        <button 
          className={`tab ${activeTab === 'lexicon' ? 'active' : ''}`}
          onClick={() => setActiveTab('lexicon')}
        >
          📖 Словарь
        </button>
        <button 
          className={`tab ${activeTab === 'writing' ? 'active' : ''}`}
          onClick={() => setActiveTab('writing')}
        >
          ⚙️ Настройки
        </button>
        <button 
          className={`tab ${activeTab === 'examples' ? 'active' : ''}`}
          onClick={() => setActiveTab('examples')}
        >
          💡 Примеры
        </button>
        <button 
          className={`tab ${activeTab === 'channel' ? 'active' : ''}`}
          onClick={() => setActiveTab('channel')}
        >
          📡 Анализ канала
        </button>
      </div>

      {/* Контент табов */}
      <div className="tab-content">
        {activeTab === 'summary' && (
          <div className="section">
            <h3>Описание стиля</h3>
            <p className="description">
              Опишите ваш авторский стиль - как вы обычно пишете, какой тон используете.
            </p>
            <textarea
              value={style.style_summary}
              onChange={(e) => handleStyleChange('style_summary', e.target.value)}
              placeholder="Например: Дружелюбный и информативный стиль с юмором..."
              rows={6}
              disabled={saving}
            />
          </div>
        )}

        {activeTab === 'lexicon' && (
          <div className="section">
            <div className="lexicon-section">
              <h3>Предпочитаемые слова</h3>
              <p className="description">
                Слова и выражения, которые вы часто используете и любите.
              </p>
              <textarea
                value={style.positive_lexicon.join(', ')}
                onChange={(e) => handleLexiconChange('positive_lexicon', e.target.value)}
                placeholder="отлично, замечательно, интересно..."
                rows={3}
                disabled={saving}
              />
            </div>

            <div className="lexicon-section">
              <h3>Избегаемые слова</h3>
              <p className="description">
                Слова и выражения, которые вы стараетесь не использовать.
              </p>
              <textarea
                value={style.negative_lexicon.join(', ')}
                onChange={(e) => handleLexiconChange('negative_lexicon', e.target.value)}
                placeholder="шаблонно, банально, скучно..."
                rows={3}
                disabled={saving}
              />
            </div>
          </div>
        )}

        {activeTab === 'writing' && (
          <div className="section">
            <h3>Настройки стиля</h3>
            
            <div className="setting-group">
              <label>Тон:</label>
              <select
                value={style.writing_style.tone}
                onChange={(e) => handleWritingStyleChange('tone', e.target.value)}
                disabled={saving}
              >
                <option value="formal">Формальный</option>
                <option value="neutral">Нейтральный</option>
                <option value="friendly">Дружелюбный</option>
                <option value="casual">Непринужденный</option>
                <option value="humorous">С юмором</option>
              </select>
            </div>

            <div className="setting-group">
              <label>Формальность:</label>
              <select
                value={style.writing_style.formality}
                onChange={(e) => handleWritingStyleChange('formality', e.target.value)}
                disabled={saving}
              >
                <option value="very_formal">Очень формально</option>
                <option value="formal">Формально</option>
                <option value="casual">Неформально</option>
                <option value="very_casual">Очень неформально</option>
              </select>
            </div>

            <div className="setting-group">
              <label>Использование эмодзи:</label>
              <select
                value={style.writing_style.emoji_usage}
                onChange={(e) => handleWritingStyleChange('emoji_usage', e.target.value)}
                disabled={saving}
              >
                <option value="none">Не использовать</option>
                <option value="minimal">Минимально</option>
                <option value="moderate">Умеренно</option>
                <option value="frequent">Часто</option>
              </select>
            </div>

            <div className="setting-group">
              <label>Длина абзацев:</label>
              <select
                value={style.writing_style.paragraph_length}
                onChange={(e) => handleWritingStyleChange('paragraph_length', e.target.value)}
                disabled={saving}
              >
                <option value="short">Короткие</option>
                <option value="medium">Средние</option>
                <option value="long">Длинные</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="section">
            <h3>Примеры постов</h3>
            <p className="description">
              Добавьте примеры ваших постов, чтобы AI лучше понял ваш стиль.
            </p>
            
            {style.examples.map((example, index) => (
              <div key={index} className="example-item">
                <div className="example-content">{example}</div>
                <button 
                  className="remove-example"
                  onClick={() => removeExample(index)}
                  disabled={saving}
                >
                  ❌
                </button>
              </div>
            ))}

            <button 
              className="add-example"
              onClick={addExample}
              disabled={saving}
            >
              ➕ Добавить пример
            </button>

            {style.examples.length === 0 && (
              <div className="no-examples">
                Примеры постов не добавлены. Добавьте несколько примеров для лучшего качества генерации.
              </div>
            )}
          </div>
        )}

        {activeTab === 'channel' && (
          <div className="section">
            <h3>Анализ Telegram канала</h3>
            <p className="description">
              Введите ссылку на публичный Telegram канал, и мы автоматически настроим ваш стиль на основе последних 15 постов.
            </p>
            
            <div className="channel-input-group">
              <input
                type="text"
                value={channelUrl}
                onChange={(e) => setChannelUrl(e.target.value)}
                placeholder="Например: t.me/channelname или @channelname"
                disabled={isAnalyzing || saving}
                className="channel-input"
              />
              
              <button 
                className="analyze-button"
                onClick={analyzeChannel}
                disabled={isAnalyzing || saving || !channelUrl.trim()}
              >
                {isAnalyzing ? '🔄 Анализируем...' : '🔍 Анализировать канал'}
              </button>
            </div>

            {isAnalyzing && (
              <div className="analysis-progress">
                <div className="progress-indicator">
                  <div className="spinner small"></div>
                  <span>{analysisProgress}</span>
                </div>
                <div className="progress-description">
                  Это может занять несколько минут. Мы получаем последние посты и анализируем их стиль с помощью ИИ.
                </div>
              </div>
            )}

            <div className="channel-help">
              <h4>💡 Как это работает:</h4>
              <ul>
                <li>🔗 Вставьте ссылку на публичный канал</li>
                <li>📊 Мы получим последние 15 постов</li>
                <li>🧠 ИИ проанализирует стиль письма</li>
                <li>✨ Автоматически заполним ваш профиль</li>
              </ul>
              
              <h4>📋 Поддерживаемые форматы:</h4>
              <ul>
                <li><code>t.me/channelname</code></li>
                <li><code>@channelname</code></li>
                <li><code>channelname</code> (без префиксов)</li>
              </ul>
              
              <div className="channel-note">
                <strong>Важно:</strong> Канал должен быть публичным и содержать текстовые посты. 
                Анализ работает только с каналами, в которых нет ограничений на просмотр контента.
              </div>
            </div>
          </div>
        )}
      </div>

      {saving && (
        <div className="saving-indicator">
          <div className="spinner small"></div>
          Сохранение...
        </div>
      )}
    </div>
  );
};

export default StyleEditor; 